import { useEffect, useRef, useState } from 'react';

import type { ColorName } from '../Input2/buttonClasses';
import type { Size } from '../Input2/types';
import { Button } from '../Input2';
import { General } from '../StrokeIcons';

/** How long "Copied" / "Couldn't copy" stays up before the label returns to "Copy". */
export const COPY_FEEDBACK_MS = 1500;

type CopyState = 'idle' | 'copied' | 'failed';

export type CopyFeedbackButtonProps = {
  /** Put the text on the clipboard. Throwing or rejecting shows "Couldn't copy". */
  onCopy: () => Promise<void> | void;
  size?: Size;
  /** Text style (no fill) is the default: the copy action is secondary wherever this is used. */
  text?: boolean;
  tone?: 'filled' | 'outline' | 'soft';
  /** Idle colour. The failed state is always red. */
  color?: ColorName;
  className?: string;
  'data-test'?: string;
};

/**
 * "Copy" that answers for itself: the label flips to "Copied" or "Couldn't copy" for a moment and
 * then returns, so the rep never has to guess whether the click did anything (clipboard access is
 * denied in some contexts, and a silent button there reads as broken). Same feedback pattern as the
 * sequence text strip's copy button.
 */
export function CopyFeedbackButton({
  onCopy,
  size = 'xs',
  text = true,
  tone,
  color = 'gray',
  className,
  'data-test': dataTest = 'copy-feedback-button',
}: CopyFeedbackButtonProps) {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (feedbackTimer.current != null) clearTimeout(feedbackTimer.current);
    },
    [],
  );

  const copy = async () => {
    let ok = true;
    try {
      await onCopy();
    } catch {
      ok = false;
    }
    setCopyState(ok ? 'copied' : 'failed');
    if (feedbackTimer.current != null) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setCopyState('idle'), COPY_FEEDBACK_MS);
  };

  return (
    <Button
      size={size}
      text={text}
      tone={tone}
      color={copyState === 'failed' ? 'red' : color}
      icon={copyState === 'copied' ? <General.Check /> : <General.Copy01 />}
      onClick={copy}
      className={className}
      data-test={dataTest}
      data-copy-state={copyState}>
      {copyState === 'copied' ? 'Copied' : copyState === 'failed' ? "Couldn't copy" : 'Copy'}
    </Button>
  );
}
